import React, { useState, useEffect, useContext } from "react";
import { Box, DialogTitle, Typography, IconButton, DialogContent, Button, Fade, Grid, Checkbox, CircularProgress, Drawer, Divider, Dialog, DialogActions, Link } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isAndroid, isIOS } from "src/utils/platformUtils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { WidgetContext } from "src/contexts/WidgetContext";

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  /** When provided, back arrow is visible and navigates back (e.g. to PaymentMethodMenu) instead of just closing */
  onBack?: () => void;
  onCancelSubscriptionClick?: () => void;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({ open, onClose, onConfirm, isDeleting, onBack, onCancelSubscriptionClick }) => {
  const { aiSettings } = useContext(WidgetContext);
  // 2. Añadir estado para controlar el checkbox
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  // Reset checkbox state when the dialog is closed
  useEffect(() => {
    if (!open) {
      setIsConfirmed(false);
      setIsConfirmDialogOpen(false);
    }
  }, [open]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isConfirmed) {
      setIsConfirmDialogOpen(true);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      // onOpen={() => {}}
      // onClose={platformSelected.credentials_type === "yahoo_auth" ? handleCloseYahoo : handleClose}
      onClose={onClose}
      // sx={{ pointerEvents: "none", zIndex: 1200 }}
      hideBackdrop={true}
      sx={{ pointerEvents: "none" }}
      ModalProps={{
        keepMounted: true,

        // No cubrir la barra superior: safe-area + 56px (misma cifra que restas en height)
        sx: {
          zIndex: 1500,
          top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
        },
      }}
      PaperProps={{
        sx: {
          width: {
            xs: "100%",
            sm: "720px",
            md: "720px",
            lg: "720px",
          },
          margin: {
            xs: "0",
            sm: "0 auto",
            md: "0 auto",
            lg: "0 auto",
          },
          height: isIOS || isAndroid
            ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
            : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"),          // height: "calc(100svh - env(safe-area-inset-top) - 56px)",
          // height: isIOS || isAndroid ? "calc(100vh - env(safe-area-inset-top) - 56px)" : "calc(100vh  - 56px)",

          border: `0.5px solid ${PureLightThemeCaaS.colors.alpha.white[70]}`,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          bgcolor: "#EDEDED",
        },
      }}
    >
      <Fade in={open}>
        <Box sx={{ width: "100%", pointerEvents: "auto" }}>
          <DialogTitle sx={{ bgcolor: "#EDEDED", borderTopLeftRadius: 5, borderTopRightRadius: 5, pb: 2, p: 1 }}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                // ADD THIS LINE to respect the bottom safe area on all devices
                paddingBottom: isAndroid ? "max(env(safe-area-inset-bottom), 15px)" : "0px",
              }}
            >

              <IconButton aria-label="back" onClick={onBack}>
                <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
              </IconButton>

              <Typography variant="h4" color={PureLightThemeCaaS.colors.alpha.black[100]} textAlign="center" p={0}>
                Delete Your Account and Data
              </Typography>
              <IconButton aria-label="close" onClick={onClose}>
                {/* <IconButton aria-label="close" onClick={platformSelected.credentials_type === "yahoo_auth" ? handleCloseYahoo : handleClose}> */}
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          {/* Divider for separation between the title and the cards */}
          <Divider
            sx={{
              margin: {
                xs: "auto",
                md: "0px",
              },
              width: "100%",
              bgcolor: "#000000",
              my: 0.5,
            }}
          />
          <DialogContent sx={{ bgcolor: "#EDEDED" }}>
            <form onSubmit={handleSubmit}>
              {" "}
              {/* 3. Usar un manejador de submit local */}
              <Grid container spacing={1} justifyContent="center" sx={{ padding: 0 }}>
                {/* <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Typography variant="h4" color={PureLightThemeCaaS.colors.alpha.black[100]} textAlign="center" p={0}>
                    Delete Your Account and Data
                  </Typography>
                </Grid> */}

                {/* Box for authorization text and checkbox */}
                <Box width="80%" sx={{ margin: "auto", pt: 4 }}>
                  <Box display="flex" alignItems="flex-start">
                    <Checkbox
                      checked={isConfirmed} // 4. Usar el estado aquí
                      onChange={(e) => setIsConfirmed(e.target.checked)} // 5. Actualizar el estado aquí
                      sx={{
                        padding: "0",
                        pt: 0.2,
                        boxShadow: "none",
                        marginRight: 1,
                        "&.Mui-checked": {
                          color: PureLightThemeCaaS.colors.alpha.black[100],
                        },
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    />
                    <Typography variant="h6" color={PureLightThemeCaaS.colors.alpha.black[100]} textAlign="left">
                      By continuing, your account and all associated data will be permanently deleted. This action cannot be undone.
                    </Typography>
                  </Box>
                </Box>
                <Grid item xs={12} sx={{ textAlign: "center", mt: 1 }}>
                  <Button
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={!isConfirmed || isDeleting}
                    sx={{
                      bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
                      width: "320px",
                      height: "52px",
                      p: 0,
                      "&.Mui-disabled": {
                        bgcolor: PureLightThemeCaaS.colors.alpha.gray[30],
                        color: PureLightThemeCaaS.colors.alpha.black[50],
                      },
                    }}
                  >
                    {isDeleting ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Delete My Account"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </DialogContent>
        </Box>
      </Fade>

      <Dialog
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          zIndex: 1600,
        }}
        PaperProps={{
          sx: {
            bgcolor: "#EDEDED",
            borderRadius: 1,
            border: "0.5px solid #000000",
            boxShadow: "4px 4px 12px rgba(0,0,0,0.25)",
            p: 0.5,
            
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              position: "fixed",
              backdropFilter: "blur(1px) !important",
              WebkitBackdropFilter: "blur(1px) !important",
              backgroundColor: "rgba(0, 0, 0, 0.35)",      
                     
            },
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: PureLightThemeCaaS.colors.alpha.black[100], fontWeight: 700, p: 1, pb: 1 }}>
          Delete Your RotoGPT Account?
        </DialogTitle>
         {/* Divider for separation between the title and the cards */}
         <Divider
            sx={{
              margin: {
                xs: "auto",
                md: "0px",
              },
              width: "100%",
              bgcolor: "#000000",
              my: 0.5,
            }}
          />
        <DialogContent sx={{ p: 2,  scrollbarWidth: "none", }}>
          <Typography variant="body2" color={PureLightThemeCaaS.colors.alpha.black[100]} sx={{ mb: 1 }}>
            Please read the following carefully. This action cannot be undone once you've done this.
          </Typography>
          <Typography variant="body2" color={PureLightThemeCaaS.colors.alpha.black[100]} sx={{ fontWeight: "bold", mb: 1 }}>
            What happens next:
          </Typography>
          <Typography variant="body2" color={PureLightThemeCaaS.colors.alpha.black[100]} sx={{ mb: 1 }}>
            <span style={{ fontWeight: "bold" }}>Immediate Access & Billing:</span> Your login access will be revoked immediately. Any active paid subscriptions will be canceled effectively today, and you will not be charged again.
          </Typography>
          {/* <Typography variant="body2" color={PureLightThemeCaaS.colors.alpha.black[100]} sx={{ mb: 1 }}>
            <span style={{ fontWeight: "bold" }}>30-Day Recovery Window:</span> Your data will be placed in a "soft delete" state for 30 days. This acts as a safety net if you change your mind. After 30 days, all account data, history, and settings will be permanently purged from our servers.
          </Typography> */}
          <Typography variant="body2" color={PureLightThemeCaaS.colors.alpha.black[100]} sx={{ mb: 1 }}>
            <span style={{ fontWeight: "bold" }}>Forfeiture of Credits:</span> Any remaining credits or unused membership balances will be forfeited immediately upon account deletion.
          </Typography>
          <Typography variant="body2" color={PureLightThemeCaaS.colors.alpha.black[100]} sx={{ mb: 2 }}>
            <span style={{ fontWeight: "bold" }}>No Refunds:</span> Deleting your account does not automatically trigger a refund for the current billing period.
          </Typography>
          <Typography variant="body2" color={PureLightThemeCaaS.colors.alpha.black[100]}>
            <span style={{ fontWeight: "bold" }}>Note:</span> If you only wish to stop future payments but keep your data and credits, please{" "}
            {/* <Link
              component="button"
              variant="body2"
              onClick={() => {
                setIsConfirmDialogOpen(false);
                if (onCancelSubscriptionClick) {
                  onCancelSubscriptionClick();
                }
              }}
              underline="none"
              sx={{ color: "#1976d2", verticalAlign: "baseline", p: 0, m: 0, fontSize: "inherit", fontFamily: "inherit" }}
            >
              Cancel your Subscription
            </Link>{" "} */}

            Cancel your Subscription

            instead of deleting your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => setIsConfirmDialogOpen(false)}
            autoFocus
            sx={{
              flex: 1,
              whiteSpace: "nowrap",
              p: 0.5,
              bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
              "&:hover": { bgcolor: PureLightThemeCaaS.colors.alpha.orange[90] },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setIsConfirmDialogOpen(false);
              onConfirm();
            }}
            sx={{
              flex: 1,
              whiteSpace: "nowrap",
              p: 0.5,
              borderColor: PureLightThemeCaaS.colors.alpha.black[70],
              color: PureLightThemeCaaS.colors.alpha.black[100],
              "&:hover": { borderColor: PureLightThemeCaaS.colors.alpha.black[100] },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default DeleteAccountDialog;
